import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { useCurrency } from '../context/CurrencyContext';

const ProductModal = ({ product, onClose }) => {
  const { fmt } = useCurrency();
  const { addToCart } = useApp();
  const [src, setSrc] = useState(product?.image || product?.fallbackImage || '');
  const [failed, setFailed] = useState(!src);
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  if (!product) return null;
  const handleErr = () => {
    if (product.fallbackImage && src !== product.fallbackImage) {
      setSrc(product.fallbackImage);
    } else {
      setFailed(true);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="relative bg-white dark:bg-[#141416] rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-4 right-4 z-10 w-9 h-9 rounded-full border border-neutral-200 dark:border-neutral-700 flex items-center justify-center hover:bg-neutral-50 dark:hover:bg-neutral-800">
          <X size={16} />
        </button>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
          <div className="bg-neutral-50 dark:bg-neutral-900 flex items-center justify-center p-8 min-h-[400px]">
            {!failed && src ? (
              <img src={src} alt={product.name} className="max-h-[380px] object-contain" onError={handleErr} />
            ) : (
              <div className="text-center text-neutral-400">
                <div className="text-[11px] tracking-[0.15em] uppercase mb-2 opacity-60">{product.catLabel}</div>
                <div className="font-serif text-[32px] leading-tight text-neutral-500 dark:text-neutral-400">{product.name}</div>
                <div className="text-[14px] mt-2 opacity-70">{product.strength}</div>
              </div>
            )}
          </div>

          <div className="p-7 md:p-8">
            <div className="text-[11px] tracking-[0.1em] text-neutral-500 mb-2 uppercase">{product.catLabel}</div>
            <h2 className="font-serif text-[32px] text-neutral-900 dark:text-neutral-100 mb-5">{product.name}</h2>

            <div className="grid grid-cols-3 gap-3 mb-6">
              <div className="border border-neutral-200 dark:border-neutral-800 rounded-lg p-3">
                <div className="text-[10.5px] tracking-widest text-neutral-500 uppercase mb-1">Strength</div>
                <div className="text-[15px] text-neutral-900 dark:text-neutral-100">{product.strength}</div>
              </div>
              <div className="border border-neutral-200 dark:border-neutral-800 rounded-lg p-3">
                <div className="text-[10.5px] tracking-widest text-neutral-500 uppercase mb-1">Packaging</div>
                <div className="text-[15px] text-neutral-900 dark:text-neutral-100">{product.packaging}</div>
              </div>
              <div className="border border-neutral-200 dark:border-neutral-800 rounded-lg p-3">
                <div className="text-[10.5px] tracking-widest text-neutral-500 uppercase mb-1">Price</div>
                <div className="text-[15px] font-semibold text-neutral-900 dark:text-neutral-100">{fmt(product.price)}</div>
              </div>
            </div>

            <div className="border border-neutral-200 dark:border-neutral-800 rounded-lg p-4 mb-4">
              <div className="text-[11px] tracking-widest text-neutral-500 uppercase mb-2">What it does</div>
              <p className="text-[13.5px] leading-relaxed text-neutral-700 dark:text-neutral-300">{product.desc}</p>
            </div>

            <div className="border border-red-100 dark:border-red-900/40 bg-red-50/40 dark:bg-red-950/20 rounded-lg p-4 mb-6">
              <div className="text-[11px] tracking-widest text-red-700 dark:text-red-400 uppercase mb-2">Safety Note</div>
              <p className="text-[13px] leading-relaxed text-neutral-700 dark:text-neutral-300">Verify indications, dose, interactions, and legal requirements with a licensed clinician or pharmacist before use.</p>
            </div>

            <button onClick={() => { addToCart(product); onClose(); }} className="btn-brand w-full rounded-lg py-3.5 text-[15px] font-semibold">
              Add to cart
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductModal;
